/**
 * Firebase Functions v2 callable client for the OMON-144 admin chokepoint.
 *
 * Wraps `getFunctions(app, "europe-west2").httpsCallable("practitioner_role_action")`
 * so callers get a typed API surface that mirrors the CF's action envelope.
 *
 * The Firebase callable client automatically:
 *   - Attaches the current user's ID token to the request
 *   - Verifies the response shape; throws typed `FunctionsError` on
 *     `unauthenticated` / `permission-denied` / etc.
 *
 * Region MUST match the CF's deployed region. STC infra is `europe-west2`.
 */
import { getFunctions, httpsCallable, FunctionsError } from "firebase/functions";

import { app } from "../firebase";

// ── Typed payload + response shapes (mirrors the CF's action envelope) ──

export type AccessRoleValue =
  | "carer" | "planner" | "librarian" | "admin" | "auditor"
  /** Non-production only (ADR-0013 amendment): plans + catalogue. */
  | "practice-author";
export type DataScopeValue = "real" | "test" | "both";

export interface AssignPayload {
  action: "assign";
  email: string;
  role: AccessRoleValue;
  dataScope: DataScopeValue;
  careTeams?: string[];
  /** ISO 8601 datetime. Null/absent = open-ended. */
  periodEnd?: string | null;
  organizationId?: string;
}

export interface UpdatePayload {
  action: "update";
  roleDocId: string;
  role?: AccessRoleValue;
  dataScope?: DataScopeValue;
  /** ISO 8601 datetime. */
  periodEnd?: string;
}

export interface EndPayload {
  action: "end";
  roleDocId: string;
}

export interface DeletePayload {
  action: "delete";
  roleDocId: string;
}

/**
 * OMON-151: suspend a user's app access (reversible).
 *
 * Flips `_practitionerRole.active=false`. Leaves `period.end` alone.
 * Use this for App-Store-launch revocation when the admin wants to
 * block the user's iOS app without deleting the role record (the
 * `delete` action) or permanently ending it (the `end` action).
 *
 * Idempotent on the server: a `suspend` of an already-suspended role
 * returns `{kind: "idempotent_noop", ...}`.
 */
export interface SuspendPayload {
  action: "suspend";
  roleDocId: string;
}

/**
 * OMON-151: resume a previously-suspended role's app access.
 *
 * Flips `_practitionerRole.active=true`. Inverse of `SuspendPayload`.
 * Idempotent on the server.
 */
export interface ResumePayload {
  action: "resume";
  roleDocId: string;
}

export interface RefreshClaimsPayload {
  action: "refresh-claims";
  emailOrUid: string;
}

/**
 * OMON-152: provision a fresh `_person` doc for a Firebase Auth user
 * lacking one.
 *
 * Use case: developer admins, App Reviewer accounts, service accounts
 * — anyone whose Firebase Auth identity exists but who isn't sourced
 * from OneTouch sync (which auto-creates `_person` docs as a side
 * effect). Without this, `assign` would fail with
 * `failed-precondition: "Auth user ... has no _person doc"`.
 *
 * Idempotent on the server: provisioning an already-wired email
 * returns `{kind: "idempotent_noop", personDocId, authUid}` rather
 * than creating a duplicate.
 */
export interface ProvisionPersonPayload {
  action: "provision-person";
  email: string;
  /** First/given name. */
  given: string;
  /** Surname/family name. */
  family: string;
  /** Optional free-text note for provenance. */
  note?: string;
}

/**
 * OMON-195: generate a password-reset link for the user behind a _person doc.
 *
 * The CF resolves the target's Firebase Auth email server-side (personDocId →
 * uid → email), mints a one-time reset link, audits the action, and returns
 * `{email, authUid, link}` in `result`. The admin UI copies the link or
 * triggers the reset email client-side (the Admin SDK can only mint links).
 */
export interface ResetPasswordLinkPayload {
  action: "reset-password-link";
  personDocId: string;
}

/**
 * ADR-0046 phase 2b: edit person fields from the card. v1 covers the
 * identity domain. If a targeted domain is stewarded by another system
 * (OneTouch) and `takeOverDomains` doesn't name it, the CF refuses with
 * failed-precondition `TAKEOVER_REQUIRED: …` — the UI's confirm dialog
 * is a retry with consent, so promotion can't happen by accident.
 */
export interface UpdatePersonPayload {
  action: "update-person";
  personDocId: string;
  fields: {
    given?: string; family?: string; gender?: string; birthDate?: string;
    /** Person card PR 2a: contact details (the contact domain). */
    email?: string; mobile?: string;
    address?: { line?: string; city?: string; postalCode?: string };
  };
  takeOverDomains?: string[];
}

/** 2026-09-24: record, change or remove (start: null) a carer's "Soma
 *  trained" date on their `_practitioner.qualification`. */
export interface SetQualificationPayload {
  action: "set-qualification";
  personDocId: string;
  qualification: "soma-trained";
  start: string | null;
}

/**
 * Mint a PRODUCTION Firebase Auth login for someone who has none.
 *
 * The one action in this envelope that creates a credential. The server
 * generates a password, uses it once and discards it unread — the person
 * sets their own from the reset email — so nothing secret crosses this
 * boundary in either direction. Idempotent: an email that already has a
 * login returns `{kind: "idempotent_noop", authUid}`.
 *
 * Production-only by construction: the callable refuses when the caller's
 * database isn't `(default)`, so a develop build cannot mint main-pool
 * accounts.
 */
export interface CreateLoginPayload {
  action: "create-login";
  email: string;
  /** Shown in the Firebase console and on the account; not a credential. */
  displayName?: string;
}

export type ActionPayload =
  | AssignPayload
  | CreateLoginPayload
  | UpdatePayload
  | EndPayload
  | DeletePayload
  | SuspendPayload
  | ResumePayload
  | ProvisionPersonPayload
  | UpdatePersonPayload
  | SetQualificationPayload
  | ResetPasswordLinkPayload
  | RefreshClaimsPayload;

export interface ActionResult {
  ok: true;
  action: ActionPayload["action"];
  result: {
    kind: string;
    [key: string]: unknown;
  };
}

// ── Callable wrapper ────────────────────────────────────────────────

const functions = getFunctions(app, "europe-west2");
const callable = httpsCallable<ActionPayload, ActionResult>(
  functions,
  "practitioner_role_action",
);

/**
 * Invoke the CF with a typed action payload. Resolves with the result
 * envelope on success; throws `FunctionsError` (with a typed `code`) on
 * any failure.
 *
 * Caller is responsible for mapping `FunctionsError.code` to user-facing
 * messages — typical map:
 *   - "unauthenticated"     → "Please sign in again."
 *   - "permission-denied"   → "Admin role required."
 *   - "invalid-argument"    → "Form invalid — fix the fields shown red."
 *   - "failed-precondition" → "Account isn't fully provisioned (OMON-141)."
 *   - "not-found"           → "Couldn't find that user."
 */
export async function callPractitionerRoleAction(
  payload: ActionPayload,
): Promise<ActionResult> {
  const response = await callable(payload);
  return response.data;
}

/**
 * Type guard for the typed Firebase callable error shape. Use to discriminate
 * `FunctionsError` from other failures (network errors, JSON parse failures).
 */
export function isFunctionsError(err: unknown): err is FunctionsError {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as { code: unknown }).code === "string"
  );
}

// ─── OMON-208: the Related-people office chokepoint ───────────────────
//
// Same europe-west2 callable rail as practitioner_role_action. Verbs:
// provision | attach-number | resolve-request | list-requests | revoke | grant.
// The read verb lives here (not a client-SDK query) because a broad
// pending-status list is strict-rules-incompatible — see OMON-250.
// `grant` (ADR-0045 amendment 2026-09-21) is the office's yes: from that
// date a matching number is not access. With `requestId` it also closes
// the queued request, atomically. `list-family` (OMON-348) returns every
// family contact for Users & Roles, which lists everyone. `set-email`
// (ADR-0046 amendment 2026-09-21) sets one email on all of a person's contact
// records and takes their details over from the OneTouch sync.

export interface RelatedPersonActionPayload {
  action: "provision" | "attach-number" | "resolve-request" | "list-requests" | "revoke"
    | "grant" | "list-family" | "set-email";
  relatedPersonDocId?: string;
  /** `set-email`: every contact record of one person, and the one address. */
  relatedPersonDocIds?: string[];
  email?: string;
  requestId?: string;
  outcome?: "resolved" | "rejected";
  reason?: string;
  mobile?: string;
}

export interface RelatedPersonActionResult {
  ok?: boolean;
  action?: string;
  requests?: unknown[];
  /** `list-family` (OMON-348): one person × client link per family contact. */
  contacts?: unknown[];
  roleCleared?: boolean;
  requestResolved?: boolean;
}

const relatedPersonCallable = httpsCallable<
  RelatedPersonActionPayload,
  RelatedPersonActionResult
>(functions, "related_person_action");

export async function callRelatedPersonAction(
  payload: RelatedPersonActionPayload,
): Promise<RelatedPersonActionResult> {
  try {
    const res = await relatedPersonCallable(payload);
    return res.data;
  } catch (err) {
    const fe = err as FunctionsError;
    throw new Error(fe?.message || "The action could not be completed.");
  }
}

// ─── ADR-0045 / AFHR-031: environment access (training invites) ───────
//
// Same europe-west2 callable rail. The callable is training_invite_action
// (AFHR-027) — the SAME implementation the CLI calls, so the pane and the
// command line cannot drift. `environment` is a required parameter from
// day one (ADR-0045's shape constraint: the pane is the first COLUMN of
// the environment grid, not a training-only feature) even though
// "training" is the only provisioned value today — the server refuses
// anything else with invalid-argument rather than silently defaulting.

/** One provisioned environment, as the server describes it (ADR-0045
 *  amendment): the list is DATA, so a new tenant needs no client release. */
export interface EnvironmentSpec {
  environment: string;
  /** The environment's Identity Platform tenant, or null for production —
   *  the main pool has no tenant, which is exactly why it cannot mint. */
  tenant: string | null;
  /**
   * How access to this environment is established. The door branches on
   * this, because the two are not interchangeable:
   *
   *   "invite" — mint an account in the environment's own tenant; the
   *              person signs in with an emailed link, no password.
   *   "role"   — grant a `_practitionerRole` to a login that must
   *              ALREADY exist (production). Nothing is minted.
   */
  mechanism: "invite" | "role";
  /** Where a family invitee signs in (Circle of Support). */
  familySurface: string;
  /** Where a staff-shaped invitee signs in (the planner). */
  staffSurface: string;
  /** Grantable roles for this environment, server-defined. */
  roles: string[];
}

export interface EnvironmentInvitePayload {
  action: "invite" | "extend" | "revoke" | "reinstate" | "list" | "list-environments";
  /** Server-validated against the provisioned set; not a client literal. */
  environment: string;
  /** invite: which role to grant — family (default) or a staff role. */
  role?: string;
  /** invite: the guest's email (the identity AND the credential channel). */
  email?: string;
  /** invite / extend: expiry date, YYYY-MM-DD (must be in the future). */
  expires?: string;
  /** invite: optional given name shown inside the fiction ("Guest" if absent). */
  given?: string;
  /** extend / revoke / reinstate: the guest's uid from the list. */
  uid?: string;
}

/** One environment-access record (prod `_ops/environment_access/entries/<uid>`). */
export interface EnvironmentGuestEntry {
  uid: string;
  email: string;
  given: string;
  /** guest = an outsider whose access lapses; staff = one of ours, open-ended. */
  kind?: "guest" | "staff";
  /** YYYY-MM-DD, or null/absent for an open-ended (staff) grant. */
  expires: string | null;
  role: string;
  tenant: string;
  /** The tenant-backed environment the account lives in (#1939). Entries
   *  written before that carry none — they are all training. */
  environment?: string;
  /** ISO 8601 (serialised server-side). */
  invitedAt: string | null;
  disabledAt: string | null;
  invitedBy?: { uid?: string; personDocId?: string | null };
  personaSlug?: string;
}

export interface EnvironmentInviteResult {
  uid?: string;
  email?: string;
  created?: boolean;
  expires?: string;
  surface?: string;
  next?: string;
  disabled?: boolean;
}

/**
 * What the callable actually sends back.
 *
 * ☠️ The payload is WRAPPED — `{ok, action, result}` — exactly like
 * `practitioner_role_action`'s. This type used to claim the bare result,
 * so every caller ran `Array.isArray(res.data)` against an object, got
 * `false`, and silently fell back to an empty list. The environment and
 * role dropdowns had therefore been empty since the pane shipped in
 * v1.4.42, and the guest list rendered "no guests" regardless of how many
 * there were: a parse bug that looks exactly like an empty database,
 * which is why it survived three releases.
 */
interface EnvironmentInviteEnvelope {
  ok: boolean;
  action: string;
  result: EnvironmentInviteResult | EnvironmentGuestEntry[] | EnvironmentSpec[];
}

const environmentInviteCallable = httpsCallable<
  EnvironmentInvitePayload,
  EnvironmentInviteEnvelope
>(functions, "training_invite_action");

export async function callEnvironmentInviteAction(
  payload: EnvironmentInvitePayload,
): Promise<EnvironmentInviteResult | EnvironmentGuestEntry[] | EnvironmentSpec[]> {
  try {
    const res = await environmentInviteCallable(payload);
    // Unwrap here, once, so no caller has to know the envelope exists —
    // and so a future action can't reintroduce the same mistake. The
    // server always wraps; a defensive fallback to the raw payload would
    // only widen the return type and hide the next shape change.
    return res.data.result;
  } catch (err) {
    const fe = err as FunctionsError;
    throw new Error(fe?.message || "The action could not be completed.");
  }
}

// ─── ADR-0048: promote a catalogue item from training into production ──
//
// The only call in the planner that causes a write to ANOTHER environment's
// database. It is offered solely on the training build, to an admin or
// practice-author (see catalogue/promote-rules.ts) — and the server refuses
// every case the client might get wrong.

export interface CataloguePromotePayload {
  collection: string;
  docId: string;
}

export interface CataloguePromoteResult {
  promoted: boolean;
  collection: string;
  docId: string;
  /** "first" the first time production adopts it, "update" after that. */
  kind: "first" | "update";
  auditEventId: string;
}

const cataloguePromoteCallable = httpsCallable<
  CataloguePromotePayload,
  CataloguePromoteResult
>(functions, "catalogue_promote");

export async function callCataloguePromote(
  payload: CataloguePromotePayload,
): Promise<CataloguePromoteResult> {
  try {
    const res = await cataloguePromoteCallable(payload);
    return res.data;
  } catch (err) {
    const fe = err as FunctionsError;
    // The server's refusals are written for the person who pressed the
    // button ("Promote its parent first — production has no …"), so they are
    // surfaced verbatim rather than replaced with a generic failure.
    throw new Error(fe?.message || "The item could not be promoted.");
  }
}

// ─── catalogue_withdraw (ADR-0048 phase 2) ─────────────────────────────
//
// The door back: production declines to keep a promotion. Offered only in
// the production build, to an admin or librarian, and only on items that
// arrived by promotion (see catalogue/provenance-view.ts). The server
// refuses everything else — in particular it refuses items production
// authored itself, so this is never a general catalogue delete.

export interface CatalogueWithdrawPayload {
  collection: string;
  docId: string;
}

export interface CatalogueWithdrawResult {
  withdrawn: boolean;
  collection: string;
  docId: string;
  /** True when someone had already withdrawn it — a no-op, not a failure. */
  alreadyWithdrawn: boolean;
  auditEventId: string | null;
}

const catalogueWithdrawCallable = httpsCallable<
  CatalogueWithdrawPayload,
  CatalogueWithdrawResult
>(functions, "catalogue_withdraw");

export async function callCatalogueWithdraw(
  payload: CatalogueWithdrawPayload,
): Promise<CatalogueWithdrawResult> {
  try {
    const res = await catalogueWithdrawCallable(payload);
    return res.data;
  } catch (err) {
    const fe = err as FunctionsError;
    // Same reasoning as promotion: the refusals name the actual problem
    // ("This item was authored in production, so there is no promotion to
    // withdraw"), which is more useful than anything generic.
    throw new Error(fe?.message || "The item could not be withdrawn.");
  }
}

